import { baseApi, defaultQueryFreshness } from "./baseApi";
import type {
  ConventionEvent,
  ConventionFavorite,
  ConventionsQuery,
  ConventionLocationResolution,
} from "../../api/conventions";

export const conventionsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getConventions: builder.query<ConventionEvent[], ConventionsQuery | void>({
      query: (params) => ({ url: "/conventions", method: "GET", params }),
      providesTags: [{ type: "Conventions", id: "LIST" }],
      ...defaultQueryFreshness,
    }),
    getConventionById: builder.query<ConventionEvent, string>({
      query: (id) => ({ url: `/conventions/${id}`, method: "GET" }),
      providesTags: (_res, _err, id) => [{ type: "Conventions", id: String(id) }],
      ...defaultQueryFreshness,
    }),
    resolveConventionLocation: builder.query<
      ConventionLocationResolution,
      { zipCode: string; countryCode?: string }
    >({
      query: ({ zipCode, countryCode }) => ({
        url: "/conventions/resolve-location",
        method: "GET",
        params: { zipCode, countryCode },
      }),
      ...defaultQueryFreshness,
    }),
    getConventionFavorites: builder.query<ConventionFavorite[], string>({
      query: (userId) => ({ url: `/conventions/favorites/${userId}`, method: "GET" }),
      providesTags: (_res, _err, userId) => [{ type: "Conventions", id: `favorites:${userId}` }],
      ...defaultQueryFreshness,
    }),
    getSavedConventionEvents: builder.query<ConventionEvent[], string>({
      query: (userId) => ({ url: `/conventions/favorites/${userId}/events`, method: "GET" }),
      providesTags: (_res, _err, userId) => [{ type: "Conventions", id: `favorites:events:${userId}` }],
      ...defaultQueryFreshness,
    }),
    addConventionFavorite: builder.mutation<unknown, { conventionIdOrUserId: string; maybeConventionId?: string }>({
      query: ({ conventionIdOrUserId, maybeConventionId }) => ({
        url: "/conventions/favorites",
        method: "POST",
        data: { conventionId: maybeConventionId ?? conventionIdOrUserId },
      }),
      invalidatesTags: [{ type: "Conventions", id: "LIST" }],
    }),
    removeConventionFavorite: builder.mutation<unknown, { userId: string; conventionId: string }>({
      query: ({ userId, conventionId }) => ({
        url: `/conventions/favorites/${userId}/${conventionId}`,
        method: "DELETE",
      }),
      invalidatesTags: (_res, _err, { userId }) => [{ type: "Conventions", id: `favorites:${userId}` }],
    }),
    updateConventionFavoriteReminder: builder.mutation<ConventionFavorite, { conventionId: string; payload: { reminderEnabled?: boolean; reminderAt?: string; reminderLeadDays?: number } }>({
      query: ({ conventionId, payload }) => ({
        url: `/conventions/favorites/${conventionId}/reminder`,
        method: "PATCH",
        data: payload,
      }),
      invalidatesTags: (_res, _err, { conventionId }) => [{ type: "Conventions", id: String(conventionId) }],
    }),
  }),
});

export const {
  useGetConventionsQuery,
  useGetConventionByIdQuery,
  useResolveConventionLocationQuery,
  useGetConventionFavoritesQuery,
  useGetSavedConventionEventsQuery,
  useAddConventionFavoriteMutation,
  useRemoveConventionFavoriteMutation,
  useUpdateConventionFavoriteReminderMutation,
} = conventionsApi;
